import { Delta } from "@vueup/vue-quill";

const NON_BREAKING_SPACE_REGEX = /[\u00A0\u202F\u2007]/g;
const PRESERVED_TAB_PREFIX_REGEX = /^(?:\u200B\t)+/;

const normalizeNonBreakingSpaces = (value: string) => {
  return value.replace(NON_BREAKING_SPACE_REGEX, " ");
};

const sanitizeDeltaTextOps = (delta: any) => {
  if (!delta?.ops || !Array.isArray(delta.ops)) {
    return delta;
  }

  let changed = false;
  const nextOps = delta.ops.map((op: any) => {
    if (typeof op?.insert !== "string") {
      return op;
    }

    const sanitizedInsert = normalizeNonBreakingSpaces(op.insert);
    if (sanitizedInsert === op.insert) {
      return op;
    }

    changed = true;
    return {
      ...op,
      insert: sanitizedInsert,
    };
  });

  if (!changed) {
    return delta;
  }

  return new Delta(nextOps);
};

export const useInitializeClipboard = (quill: any) => {
  const clipboard = quill.getModule("clipboard");

  clipboard.addMatcher("SPAN", useClipboardSpanMatcher());
  clipboard.addMatcher("U", useClipboardSpanMatcher());
  clipboard.addMatcher("P", useClipboardParagraphMatcher());
  clipboard.addMatcher("DIV", useClipboardDivMatcher());
  clipboard.addMatcher(Node.TEXT_NODE, (node: any, delta: any) => {
    const text = node.data as string;
    const sanitizedText = normalizeNonBreakingSpaces(text);
    const hasPreservedLeadingTabs = PRESERVED_TAB_PREFIX_REGEX.test(text);
    const containsTabs = text.includes("\t");

    if (text.startsWith("\t") || hasPreservedLeadingTabs || containsTabs) {
      // If Quill already made an insert op with attributes, just
      // replace the text part with the raw text from the node.
      if (
        delta.ops &&
        delta.ops.length > 0 &&
        typeof delta.ops[0].insert === "string"
      ) {
        const nextOps = delta.ops.map((op: any, index: number) => {
          if (index === 0) {
            return {
              ...op,
              insert: sanitizedText,
            };
          }

          if (typeof op?.insert !== "string") return op;

          return {
            ...op,
            insert: normalizeNonBreakingSpaces(op.insert),
          };
        });

        return new Delta(nextOps);
      }

      // Fallback: if for some reason there are no ops,
      // create one with the full raw text.
      return new Delta().insert(sanitizedText);
    }

    return sanitizeDeltaTextOps(delta);
  });

  // Fix for scroll jumping to top on paste
  // We use a capture-phase listener to save the scroll position before Quill's handler runs
  quill.root.addEventListener(
    "paste",
    (e: ClipboardEvent) => {
      const scrollContainer =
        document.getElementsByClassName("page-container")[0];
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;

      // Restore scroll position after the paste operation completes
      setTimeout(() => {
        scrollContainer.scrollTop = scrollTop;
      }, 10);
    },
    true
  );
};
